import { useEffect, useState } from 'react';

import { useKioskSession } from '@/lib/kioskSession';
import { fetchAssociateStatus } from '@/services/associateStatus';

/**
 * Loads the status card once, when the screen appears [KAS-1].
 *
 * **Once, and never again.** No polling and no refetch: the card describes an assignment, not a live feed,
 * and a candidate standing at a kiosk for two minutes does not need it re-read. A poll here would also turn
 * one person's visit into a stream of Bullhorn reads on a shared credential.
 *
 * It loads only when there is nothing in the session already, so arriving from KID-5's *reconfirm* option
 * with the card in hand does not fetch it a second time.
 */
export function useAssociateStatus(): { readonly loading: boolean } {
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const cards = useKioskSession((state) => state.associateCards);
  const setAssociateCards = useKioskSession((state) => state.setAssociateCards);
  /*
   * `settled` rather than `loading`, and the difference matters: setting a loading flag synchronously inside
   * the effect makes the first paint a wasted render, and ESLint flags it as cascading. Loading is DERIVED
   * from what is actually true — there is somebody to look up, no card yet, and the request has not come
   * back — so nothing has to be set before the request is even made.
   */
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (cards !== undefined || candidateId === undefined) return;

    let live = true;

    void fetchAssociateStatus(candidateId).then((outcome) => {
      // The screen can unmount mid-flight — the idle timer clears the session and returns to Welcome — and
      // writing a card into a session that has just ended would carry one candidate's assignment into the
      // next person's visit.
      if (!live) return;
      if (outcome.kind === 'cards') setAssociateCards(outcome.cards);
      setSettled(true);
    });

    return () => {
      live = false;
    };
    // `cards` is deliberately absent: including it would re-run the effect the moment the card lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, setAssociateCards]);

  return { loading: candidateId !== undefined && cards === undefined && !settled };
}
